import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  GetSegmentContactsResponse,
  GetSegmentsResponse,
  ISegment,
} from 'types/segments/segments.types';
import * as segmentService from '../../services/segmentService/segmentService';
import {
  createSegmentFailure,
  createSegmentSuccess,
  deleteSegmentFailure,
  deleteSegmentSuccess,
  getSegmentContactsFailure,
  getSegmentContactsSuccess,
  getSegmentFailure,
  getSegmentsFailure,
  getSegmentsSuccess,
  getSegmentSuccess,
  updateSegmentFailure,
  updateSegmentSuccess,
  uploadSegmentContactsFailure,
  uploadSegmentContactsSuccess,
  exportSegmentContactsFailure,
  exportSegmentContactsSuccess,
  removeContactFromSegmentFailure,
  removeContactFromSegmentSuccess,
  addContactsToSegmentFailure,
  addContactsToSegmentSuccess,
} from './segments.action';
import {
  CREATE_SEGMENT_REQUEST,
  CreateSegmentRequestAction,
  DELETE_SEGMENT_REQUEST,
  DeleteSegmentRequestAction,
  GET_SEGMENT_CONTACTS_REQUEST,
  GET_SEGMENT_REQUEST,
  GET_SEGMENTS_REQUEST,
  GetSegmentContactsRequestAction,
  GetSegmentRequestAction,
  GetSegmentsRequestAction,
  UPDATE_SEGMENT_REQUEST,
  UpdateSegmentRequestAction,
  UPLOAD_SEGMENT_CONTACTS_REQUEST,
  UploadSegmentContactsRequestAction,
  EXPORT_SEGMENT_CONTACTS_REQUEST,
  ExportSegmentContactsRequestAction,
  REMOVE_CONTACT_FROM_SEGMENT_REQUEST,
  RemoveContactFromSegmentRequestAction,
  ADD_CONTACTS_TO_SEGMENT_REQUEST,
  AddContactsToSegmentRequestAction,
} from './segments.types';

function* getSegmentsWorker(action: GetSegmentsRequestAction): SagaIterator {
  try {
    const response: GetSegmentsResponse = yield call(
      segmentService.getSegments,
      action.payload
    );
    yield put(getSegmentsSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch segments';
    yield put(getSegmentsFailure(errorMessage));
  }
}

function* getSegmentWorker(action: GetSegmentRequestAction): SagaIterator {
  try {
    const response: ISegment = yield call(
      segmentService.getSegmentById,
      action.payload
    );
    yield put(getSegmentSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch segment';
    yield put(getSegmentFailure(errorMessage));
  }
}

function* createSegmentWorker(
  action: CreateSegmentRequestAction
): SagaIterator {
  try {
    const response: ISegment = yield call(
      segmentService.createSegment,
      action.payload
    );
    yield put(createSegmentSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch segment';
    yield put(createSegmentFailure(errorMessage));
  }
}

function* updateSegmentWorker(
  action: UpdateSegmentRequestAction
): SagaIterator {
  try {
    const response: ISegment = yield call(
      segmentService.updateSegment,
      action.payload
    );
    yield put(updateSegmentSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update segment';
    yield put(updateSegmentFailure(errorMessage));
  }
}

function* deleteSegmentWorker(
  action: DeleteSegmentRequestAction
): SagaIterator {
  try {
    yield call(segmentService.deleteSegment, action.payload);
    yield put(deleteSegmentSuccess(action.payload));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to delete segment';
    yield put(deleteSegmentFailure(errorMessage));
  }
}

function* uploadSegmentContactsWorker(
  action: UploadSegmentContactsRequestAction
): SagaIterator {
  try {
    yield call(segmentService.uploadSegmentContacts, action.payload);
    yield put(uploadSegmentContactsSuccess());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to upload segment contacts';
    yield put(uploadSegmentContactsFailure(errorMessage));
  }
}

function* getSegmentContactsWorker(
  action: GetSegmentContactsRequestAction
): SagaIterator {
  try {
    const response: GetSegmentContactsResponse = yield call(
      segmentService.getSegmentContacts,
      action.payload
    );
    yield put(getSegmentContactsSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch segment contacts';
    yield put(getSegmentContactsFailure(errorMessage));
  }
}

function* exportSegmentContactsWorker(
  action: ExportSegmentContactsRequestAction
): SagaIterator {
  try {
    const blob: Blob = yield call(
      segmentService.exportSegmentContacts,
      action.payload
    );
    yield put(exportSegmentContactsSuccess(blob));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to export segment contacts';
    yield put(exportSegmentContactsFailure(errorMessage));
  }
}

function* removeContactFromSegmentWorker(
  action: RemoveContactFromSegmentRequestAction
): SagaIterator {
  try {
    const { segmentId, contactId } = action.payload;
    yield call(segmentService.removeContactFromSegment, segmentId, contactId);
    yield put(removeContactFromSegmentSuccess(contactId));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to remove contact from segment';
    yield put(removeContactFromSegmentFailure(errorMessage));
  }
}

function* addContactsToSegmentWorker(
  action: AddContactsToSegmentRequestAction
): SagaIterator {
  try {
    const { segmentId, contactIds } = action.payload;
    yield call(segmentService.addContactsToSegment, segmentId, {
      contactIds,
    });
    yield put(addContactsToSegmentSuccess());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to add contacts to segment';
    yield put(addContactsToSegmentFailure(errorMessage));
  }
}

export function* segmentsSaga(): SagaIterator {
  yield takeLatest(GET_SEGMENTS_REQUEST, getSegmentsWorker);
  yield takeLatest(GET_SEGMENT_REQUEST, getSegmentWorker);
  yield takeLatest(CREATE_SEGMENT_REQUEST, createSegmentWorker);
  yield takeLatest(UPDATE_SEGMENT_REQUEST, updateSegmentWorker);
  yield takeLatest(DELETE_SEGMENT_REQUEST, deleteSegmentWorker);
  yield takeLatest(
    UPLOAD_SEGMENT_CONTACTS_REQUEST,
    uploadSegmentContactsWorker
  );
  yield takeLatest(GET_SEGMENT_CONTACTS_REQUEST, getSegmentContactsWorker);
  yield takeLatest(
    EXPORT_SEGMENT_CONTACTS_REQUEST,
    exportSegmentContactsWorker
  );
  yield takeLatest(
    REMOVE_CONTACT_FROM_SEGMENT_REQUEST,
    removeContactFromSegmentWorker
  );
  yield takeLatest(ADD_CONTACTS_TO_SEGMENT_REQUEST, addContactsToSegmentWorker);
}
